import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { getCxQueue, updateCxQueue } from '../ReservationModule/shared/services/data.service';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { birdToast } from '../shared/components/core-ui/toast';

type OutcomeValue = 'all-good' | 'no-answer' | 'issue-reported';
type IssueTypeValue = 'maintenance' | 'house-keeping' | 'access' | 'cleanliness' | 'other';

interface CXQueueCardItem {
  id: string;
  guestName: string;
  guestPhone: string;
  reservationId: string;
  unitName: string;
  checkIn: string;
  checkOut: string;
  notes: string;
  outcome?: OutcomeValue;
  issueType?: IssueTypeValue;
}

interface CXQueueReservation {
  id: string;
  primaryGuestName?: string;
  primaryGuestPhone?: string;
  roomName?: string;
  checkIn?: number;
  checkOut?: number;
  comment?: string;
  outcomeValue?: OutcomeValue;
  issueType?: IssueTypeValue;
}

interface SpawnedTicket {
  id: string;
  queueItemId: string;
  reservationId: string;
  outcome: OutcomeValue;
  issueType: string | undefined;
  createdAt: string;
}

interface CardFormState {
  outcome?: OutcomeValue;
  issueType?: IssueTypeValue;
  notes: string;
}

const OUTCOME_OPTIONS = [
  { value: 'all-good', label: 'All Good' },
  { value: 'no-answer', label: 'No Answer' },
  { value: 'issue-reported', label: 'Issue Reported' }
];

const ISSUE_TYPE_OPTIONS: {
  value: IssueTypeValue;
  label: string;
}[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'house-keeping', label: 'House Keeping' },
  { value: 'access', label: 'Access / Key Issue' },
  { value: 'cleanliness', label: 'Cleanliness' },
  { value: 'other', label: 'Other' }
];

const formatDateTime = (value?: number) => {
  if (!value) return '-';

  const normalizedValue = value < 1_000_000_000_000 ? value * 1000 : value;

  return new Date(normalizedValue).toLocaleString('en-GB');
};

export default function CXQueueContainer() {
  const [cards, setCards] = useState<CXQueueCardItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>('');
  const [formState, setFormState] = useState<Record<string, CardFormState>>({});
  const [spawnedTickets, setSpawnedTickets] = useState<SpawnedTicket[]>([]);
  const [submittedCardIds, setSubmittedCardIds] = useState<string[]>([]);

  const fetchQueueData = () => {
    setIsLoading(true);

    return getCxQueue().then((response) => {
      if (isErrorInfo(response)) {
        setLoadError(response.errorMessage || 'Failed to load CX queue data');
        setCards([]);
        setIsLoading(false);
        return;
      }

      const nextCards = ((response.data || []) as CXQueueReservation[]).map((reservation) => ({
        id: reservation.id,
        guestName: reservation.primaryGuestName || 'Guest',
        guestPhone: reservation.primaryGuestPhone || '',
        reservationId: reservation.id,
        unitName: reservation.roomName || '-',
        checkIn: formatDateTime(reservation.checkIn),
        checkOut: formatDateTime(reservation.checkOut),
        notes: reservation.comment || '',
        outcome: reservation.outcomeValue,
        issueType: reservation.issueType
      }));

      setCards(nextCards);
      setFormState(
        nextCards.reduce<Record<string, CardFormState>>((acc, card) => {
          acc[card.id] = {
            outcome: card.outcome,
            issueType: card.issueType,
            notes: card.notes
          };

          return acc;
        }, {})
      );
      setLoadError('');
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchQueueData();
  }, []);

  const ticketCountByCard = useMemo(() => {
    return spawnedTickets.reduce<Record<string, number>>((acc, ticket) => {
      acc[ticket.queueItemId] = (acc[ticket.queueItemId] || 0) + 1;
      return acc;
    }, {});
  }, [spawnedTickets]);

  const updateCardState = (cardId: string, partial: Partial<CardFormState>) => {
    setFormState((prev) => ({
      ...prev,
      [cardId]: {
        outcome: prev[cardId]?.outcome,
        issueType: prev[cardId]?.issueType,
        notes: prev[cardId]?.notes || '',
        ...partial
      }
    }));
  };

  const handleSubmit = async (card: CXQueueCardItem) => {
    const current = formState[card.id];

    if (!current?.outcome) {
      birdToast.warning('Please select an outcome before submitting.');
      return;
    }

    if (current.outcome === 'issue-reported' && !current.issueType) {
      birdToast.warning('Please select the issue type.');
      return;
    }

    const outcome = current.outcome;
    const issueType = current.issueType;

    const promise = updateCxQueue(card.id, {
      outcome,
      issueType
    }).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }

      const ticket: SpawnedTicket = {
        id: `${card.id}-${Date.now()}`,
        queueItemId: card.id,
        reservationId: card.reservationId,
        outcome,
        issueType,
        createdAt: new Date().toLocaleString()
      };

      setSpawnedTickets((prev) => [ticket, ...prev]);
      setSubmittedCardIds((prev) => Array.from(new Set([...prev, card.id])));

      return fetchQueueData();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Submitting outcome...',
      successMessage: 'Outcome submitted successfully',
      errorPermissionMessage: 'You do not have permission to submit outcome',
      errorFailedMessage: 'Failed to submit CX queue outcome'
    });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6 md:p-8">
      <div className="space-y-6">
        <h1 className="text-5xl font-recoleta font-semibold text-neutral-500">
          Guest Follow Up Queue
        </h1>

        <div className="space-y-5">
          {isLoading ? (
            <div className="flex  items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500 shadow-sm">
              Loading CX queue...
            </div>
          ) : loadError ? (
            <div className="flex  items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm">
              <div className="max-w-lg text-center">
                <h2 className="text-3xl font-gilroy-bold font-semibold text-red-700 capitalize">
                  Unable to load CX queue
                </h2>
                <p className="mt-2 mb-0 font-gilroy-medium text-2xl text-red-600">
                  {loadError || 'Something went wrong while loading the queue.'}
                </p>
              </div>
            </div>
          ) : cards.length === 0 ? (
            <div className="flex  items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="max-w-2xl text-center">
                <h2 className="text-3xl font-gilroy-bold text-slate-800">
                  No guest follow-ups right now
                </h2>
                <p className="mt-2 mb-0 font-gilroy-medium text-xl leading-8 text-slate-500">
                  New follow-up items will appear here when guests need contact. Use this queue to
                  review stays, record outcomes, and escalate issues when needed.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {cards.map((card) => {
                const current = formState[card.id] || { notes: card.notes };
                const selectedOutcome =
                  OUTCOME_OPTIONS.find((option) => option.value === current.outcome) || null;

                const selectedIssueType =
                  ISSUE_TYPE_OPTIONS.find((option) => option.value === current.issueType) || null;
                const isIssueReported = current.outcome === 'issue-reported';
                const hasExistingOutcome = Boolean(card.outcome);
                const isSubmitted = hasExistingOutcome || submittedCardIds.includes(card.id);
                const outcomeInputId = `cx-queue-outcome-${card.id}`;
                const issueTypeInputId = `cx-queue-issue-type-${card.id}`;

                return (
                  <div
                    key={card.id}
                    className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold uppercase tracking-wide text-slate-600">
                          {card.reservationId}
                        </span>
                        {isSubmitted && (
                          <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-semibold uppercase tracking-wide text-emerald-700">
                            Submitted
                          </span>
                        )}
                      </div>
                      <span className="rounded-full bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-500">
                        CX Queue
                      </span>
                    </div>

                    <div className="mt-4 border-b border-slate-100 pb-4">
                      <h2 className="text-3xl font-gilroy-bold text-slate-800">{card.guestName}</h2>
                      <p className="mt-2 text-lg text-slate-500">{card.guestPhone}</p>
                      <p className="mt-2 text-lg text-slate-500">{card.unitName}</p>
                    </div>

                    <div className="flex flex-row justify-between mt-4 gap-2">
                      <div>
                        <p className="text-base font-semibold uppercase tracking-wide text-slate-400">
                          Check in
                        </p>
                        <p className="mt-1 text-lg font-medium text-slate-700">{card.checkIn}</p>
                      </div>
                      <div>
                        <p className="text-base font-semibold uppercase tracking-wide text-slate-400">
                          Check out
                        </p>
                        <p className="mt-1 text-lg font-medium text-slate-700">{card.checkOut}</p>
                      </div>
                    </div>

                    <div className="mt-5 space-y-4">
                      <div>
                        <label
                          htmlFor={outcomeInputId}
                          className="mb-2 block text-lg font-semibold uppercase tracking-wide text-slate-500"
                        >
                          Outcome
                        </label>
                        <Select
                          inputId={outcomeInputId}
                          value={selectedOutcome}
                          options={OUTCOME_OPTIONS}
                          isDisabled={hasExistingOutcome}
                          classNamePrefix="react-select"
                          placeholder="Select outcome"
                          onChange={(value) =>
                            updateCardState(card.id, {
                              outcome: value ? (value.value as OutcomeValue) : undefined,
                              issueType:
                                value?.value === 'issue-reported' ? current.issueType : undefined
                            })
                          }
                        />
                      </div>

                      {isIssueReported && (
                        <div>
                          <label
                            htmlFor={issueTypeInputId}
                            className="mb-2 block text-lg font-semibold uppercase tracking-wide text-slate-500"
                          >
                            Issue type
                          </label>
                          <Select
                            inputId={issueTypeInputId}
                            value={selectedIssueType}
                            options={ISSUE_TYPE_OPTIONS}
                            isDisabled={hasExistingOutcome}
                            classNamePrefix="react-select"
                            placeholder="Select issue type"
                            onChange={(value) =>
                              updateCardState(card.id, {
                                issueType: value ? (value.value as IssueTypeValue) : undefined
                              })
                            }
                          />
                        </div>
                      )}
                    </div>

                    <div className="mt-auto pt-5">
                      <div className="flex flex-col gap-3 border-t border-slate-100 pt-4">
                        <p className="mb-0 text-lg text-slate-500">
                          {ticketCountByCard[card.id]
                            ? `${ticketCountByCard[card.id]} linked ticket created`
                            : 'No linked tickets yet'}
                        </p>
                        {!hasExistingOutcome && (
                          <button
                            type="button"
                            className="inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-5 py-4 text-xl font-semibold text-white transition hover:bg-primary-700"
                            onClick={() => handleSubmit(card)}
                          >
                            Submit outcome
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
